import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash, Archive as ArchiveIcon, FilePdf, Sparkle, Eye, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Archivo() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [viewing, setViewing] = useState(null);

  const empty = {
    nombre: "",
    cliente_id: "",
    cliente_nombre: "",
    cliente_domicilio: "",
    cliente_ciudad: "",
    cliente_nif: "",
    fecha: "",
    total: 0,
    tipo: "presupuesto",
    notas: "",
    pdf_base64: "",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/archivo");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((prev) => ({
      ...prev,
      pdf_base64: ev.target.result,
      nombre: prev.nombre || file.name.replace(/\.pdf$/i, ""),
    }));
    reader.readAsDataURL(file);
  };

  const extractData = async () => {
    if (!form.pdf_base64) {
      toast.error(t("common.error"));
      return;
    }
    setExtracting(true);
    const loadingId = toast.loading(t("archivo.extracting"));
    try {
      const res = await api.post("/archivo/extraer", { pdf_base64: form.pdf_base64 });
      const d = res.data;
      setForm((prev) => ({
        ...prev,
        nombre: d.nombre || prev.nombre,
        cliente_id: d.cliente_id || "",
        cliente_nombre: d.cliente_nombre || "",
        cliente_domicilio: d.cliente_domicilio || "",
        cliente_ciudad: d.cliente_ciudad || "",
        cliente_nif: d.cliente_nif || "",
        fecha: d.fecha || "",
        total: d.total || 0,
        tipo: d.tipo || prev.tipo,
        notas: d.notas || "",
      }));
      if (d.cliente_exists) {
        toast.success(`${t("archivo.successExtract")} · ${t("archivo.clienteExists")}: ${d.cliente_nombre}`, { id: loadingId });
      } else {
        toast.success(t("archivo.successExtract"), { id: loadingId });
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail), { id: loadingId });
    } finally {
      setExtracting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pdf_base64) {
      toast.error(t("common.error"));
      return;
    }
    try {
      await api.post("/archivo", { ...form, total: parseFloat(form.total) || 0 });
      toast.success(t("common.success"));
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/archivo/${id}`);
    toast.success(t("common.success"));
    load();
  };

  const viewPdf = async (id) => {
    try {
      const res = await api.get(`/archivo/${id}`);
      setViewing(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const downloadPdf = async (id, nombre) => {
    try {
      const res = await api.get(`/archivo/${id}`);
      const link = document.createElement("a");
      link.href = res.data.pdf_base64;
      link.download = `${(nombre || "archivo").replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="archivo-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("archivo.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("archivo.subtitle")}</p>
        </div>

        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button className="bg-green-600 hover:bg-green-700 gap-2" data-testid="add-archivo-btn">
              <Plus size={18} weight="bold" /> {t("archivo.add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("archivo.add")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{t("archivo.file")}</Label>
                <Input type="file" accept="application/pdf" onChange={handleFile} required data-testid="archivo-file-input" />
                <p className="text-xs text-slate-500 mt-1">{t("archivo.uploadHint")}</p>
              </div>

              {form.pdf_base64 && (
                <Button
                  type="button"
                  onClick={extractData}
                  disabled={extracting}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2"
                  data-testid="extract-data-btn"
                >
                  <Sparkle size={16} weight="fill" />
                  {extracting ? t("archivo.extracting") : t("archivo.extract")}
                </Button>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("archivo.nombre")}</Label>
                  <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} required data-testid="archivo-nombre-input" />
                </div>
                <div>
                  <Label>{t("archivo.tipo")}</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="presupuesto">{t("archivo.presupuesto")}</SelectItem>
                      <SelectItem value="factura">{t("archivo.factura")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{t("archivo.cliente")}
                  {form.cliente_id && (
                    <span className="ml-2 text-xs text-green-700 font-semibold">
                      ✓ {t("archivo.clienteExists")}
                    </span>
                  )}
                </Label>
                <Input value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value, cliente_id: "" })} />
              </div>
              <div>
                <Label>{t("archivo.domicilio")}</Label>
                <Input value={form.cliente_domicilio} onChange={(e) => setForm({ ...form, cliente_domicilio: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("archivo.ciudad")}</Label>
                  <Input value={form.cliente_ciudad} onChange={(e) => setForm({ ...form, cliente_ciudad: e.target.value })} />
                </div>
                <div>
                  <Label>{t("archivo.nif")}</Label>
                  <Input value={form.cliente_nif} onChange={(e) => setForm({ ...form, cliente_nif: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("archivo.fecha")}</Label>
                  <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
                </div>
                <div>
                  <Label>{t("archivo.total")} (€)</Label>
                  <Input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>{t("archivo.notas")}</Label>
                <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} rows={2} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("archivo.cancel")}</Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="archivo-save-btn">{t("archivo.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <ArchiveIcon size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("archivo.empty")}
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("archivo.nombre")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("archivo.tipo")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("archivo.cliente")}</th>
                <th className="text-left text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("archivo.fecha")}</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3">{t("archivo.total")}</th>
                <th className="text-right text-xs font-bold uppercase tracking-wider text-slate-500 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`archivo-row-${a.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{a.nombre}</td>
                  <td className="px-4 py-3">
                    <Badge className={a.tipo === "factura" ? "bg-red-100 text-red-800 border-0" : "bg-green-100 text-green-800 border-0"}>
                      {a.tipo === "factura" ? t("archivo.factura") : t("archivo.presupuesto")}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{a.cliente_nombre || "—"}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{a.fecha || "—"}</td>
                  <td className="px-4 py-3 text-sm text-right font-bold">€ {(a.total || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => viewPdf(a.id)} data-testid={`view-archivo-${a.id}`}><Eye size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPdf(a.id, a.nombre)} data-testid={`download-archivo-${a.id}`}>
                      <DownloadSimple size={16} className="text-red-600" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(a.id)}>
                      <Trash size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{viewing?.nombre}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="flex-1 min-h-0">
              <iframe
                src={viewing.pdf_base64}
                title="pdf"
                className="w-full h-[70vh] border border-slate-200 rounded-lg"
              />
            </div>
          )}
          <DialogFooter>
            {viewing && (
              <Button onClick={() => downloadPdf(viewing.id, viewing.nombre)} className="gap-2 bg-red-600 hover:bg-red-700">
                <DownloadSimple size={16} /> {t("archivo.download")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
